import type { CpotIndicator, Recommendation, RecommendationCriteria, VisibleRecommendation, WorkflowRoute, WorkflowRouteId } from "./types";
import type { WorkflowState } from "../lib/state";

export const workflowRoutes: WorkflowRoute[] = [
  {
    id: "start",
    path: "/start",
    label: "Start",
    shortLabel: "Start",
    group: "entry",
    summary: "",
  },
  {
    id: "setup",
    path: "/setup",
    label: "Patient details",
    shortLabel: "Details",
    group: "entry",
    summary: "",
  },
  {
    id: "tools",
    path: "/tools",
    label: "Tools",
    shortLabel: "Tools",
    group: "tool",
    summary: "Pain score, functional assessment, and CPOT.",
  },
  {
    id: "before-surgery",
    path: "/before-surgery",
    label: "Pre-operative phase",
    shortLabel: "Pre-op",
    group: "workflow",
    summary: "",
  },
  {
    id: "during-surgery",
    path: "/during-surgery",
    label: "Intra-operative phase",
    shortLabel: "Intra-op",
    group: "workflow",
    summary: "",
  },
  {
    id: "handover",
    path: "/handover",
    label: "Post-operative phase",
    shortLabel: "Post-op",
    group: "workflow",
    summary: "",
  },
  {
    id: "itu-sedated",
    path: "/itu-sedated",
    label: "Sedated phase",
    shortLabel: "Sedated",
    group: "workflow",
    summary: "",
  },
  {
    id: "after-extubation",
    path: "/after-extubation",
    label: "Extubated phase",
    shortLabel: "Extubated",
    group: "workflow",
    summary: "",
  },
  {
    id: "oral-analgesia",
    path: "/oral-analgesia",
    label: "Oral analgesia phase",
    shortLabel: "Oral",
    group: "workflow",
    summary: "",
  },
  {
    id: "breakthrough-pain",
    path: "/breakthrough-pain",
    label: "Breakthrough pain",
    shortLabel: "Breakthrough",
    group: "workflow",
    summary: "",
  },
  {
    id: "pain-score",
    path: "/tools/pain-score",
    label: "Pain score tool",
    shortLabel: "NPS",
    group: "tool",
    summary: "Numeric Pain Score and Verbal Rating Scale.",
  },
  {
    id: "function",
    path: "/tools/function",
    label: "Function tool",
    shortLabel: "Function",
    group: "tool",
    summary: "Functional limitation assessment for breathing, coughing, mobility, and transfers.",
  },
  {
    id: "cpot",
    path: "/tools/cpot",
    label: "CPOT tool",
    shortLabel: "CPOT",
    group: "tool",
    summary: "Critical-Care Pain Observation Tool.",
  },
];

export const routeByPath = new Map(workflowRoutes.map((route) => [route.path, route]));
export const routeById = new Map(workflowRoutes.map((route) => [route.id, route]));

export const flagLabels: Record<string, string> = {
  chronicPain: "history of chronic pain",
  chronicGabapentinoid: "chronic gabapentinoid use",
  polyneuropathy: "central/peripheral polyneuropathy",
  opioidUse: "current weak/strong opioid use",
  persistentPostOpPainRisk: "persistent post-operative pain risk",
  neuropathicPain: "neuropathic features",
  methadone: "current methadone",
  ivdu: "history of IVDU",
  mentalHealth: "anxiety, depression, PTSD, or pain catastrophising",
  oralNotTolerated: "oral route not tolerated",
  functionalLimitation: "significant functional limitation",
  bmiAbove30: "BMI above 30",
  weightAbove50: "weight above 50 kg",
  ischaemicHeartDisease: "ischaemic heart disease",
  congestiveHeartFailure: "congestive heart failure",
  cerebrovascularDisease: "cerebrovascular disease",
  peripheralVascularDisease: "peripheral vascular disease",
  renalImpairment: "renal impairment",
  giBleedingHistory: "history of GI bleeding",
  coagulationDefect: "coagulation defect",
  uncontrolledHypertension: "uncontrolled hypertension",
  highDegreeHeartBlock: "high-degree heart block",
  severeBradycardia: "severe bradycardia",
  hypotensionNoradrenaline: "hypotension requiring noradrenaline above 0.1 mcg/kg/min",
  paracetamolAllergy: "paracetamol allergy",
  pregabalinAllergy: "pregabalin allergy or intolerance",
  gabapentinAllergy: "gabapentin allergy or intolerance",
  morphineAllergy: "morphine allergy",
  oxycodoneAllergy: "oxycodone allergy",
  fentanylAllergy: "fentanyl allergy",
  alfentanilAllergy: "alfentanil allergy",
  remifentanilAllergy: "remifentanil allergy",
  ketamineAllergy: "ketamine allergy",
  dexmedetomidineAllergy: "dexmedetomidine allergy",
  magnesiumAllergy: "magnesium sulphate allergy",
  nsaidAllergy: "NSAID allergy",
  buprenorphineAllergy: "buprenorphine allergy",
  dihydrocodeineAllergy: "dihydrocodeine allergy",
};

export const recommendations: Recommendation[] = [
  {
    id: "before-surgery-next-phase",
    routeIds: ["before-surgery"],
    cardId: "before-surgery-next-phase",
    cardTitle: "Progress to intra-operative phase",
    cardKind: "target",
    title: "Progress to intra-operative phase",
    strength: "recommended",
    summary: "Proceed once pre-operative analgesia has been prescribed and allergy, renal, and age exclusions have been checked.",
    details: [],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
  },
  {
    id: "pre-paracetamol",
    routeIds: ["before-surgery"],
    cardId: "pre-paracetamol",
    cardTitle: "Paracetamol",
    cardKind: "intervention",
    blockedByFlags: ["paracetamolAllergy"],
    title: "Paracetamol before surgery",
    strength: "recommended",
    summary: "Give paracetamol 1 g PO two hours before surgery.",
    details: ["The anaesthetist assessing the patient before surgery prescribes pre-operative medications."],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
  },
  {
    id: "pre-pregabalin-standard",
    routeIds: ["before-surgery"],
    cardId: "pre-pregabalin",
    cardTitle: "Pregabalin",
    cardKind: "intervention",
    blockedByFlags: ["pregabalinAllergy"],
    title: "Pregabalin when indicated",
    strength: "consider",
    summary: "Pregabalin 150 mg PO two hours before surgery.",
    details: [
      "When eGFR is above 40 and age is below 75.",
      "Use only when indicated: history of chronic pain, chronic gabapentinoid use, or central/peripheral polyneuropathy.",
      "Do not co-administer with gabapentin.",
    ],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { age: ["under75"], egfr: ["gt40"], flagsAny: ["chronicPain", "chronicGabapentinoid", "polyneuropathy"] },
  },
  {
    id: "pre-pregabalin-renal",
    routeIds: ["before-surgery"],
    cardId: "pre-pregabalin",
    cardTitle: "Pregabalin",
    cardKind: "intervention",
    blockedByFlags: ["pregabalinAllergy"],
    title: "Pregabalin dose for eGFR 30-40",
    strength: "consider",
    summary: "Pregabalin 75 mg PO two hours before surgery.",
    details: [
      "When eGFR is 30-40 and age is below 75.",
      "Use only when indicated: history of chronic pain, chronic gabapentinoid use, or central/peripheral polyneuropathy.",
      "Do not co-administer with gabapentin.",
    ],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { age: ["under75"], egfr: ["30to40"], flagsAny: ["chronicPain", "chronicGabapentinoid", "polyneuropathy"] },
  },
  {
    id: "pre-pregabalin-age-exclusion",
    routeIds: ["before-surgery"],
    cardId: "pre-pregabalin",
    cardTitle: "Pregabalin",
    cardKind: "intervention",
    blockedByFlags: ["pregabalinAllergy"],
    title: "Pregabalin not indicated",
    strength: "caution",
    summary: "Not indicated for patients above 75 or with eGFR below 30.",
    details: [],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { age: ["over75"] },
  },
  {
    id: "pre-pregabalin-renal-exclusion",
    routeIds: ["before-surgery"],
    cardId: "pre-pregabalin",
    cardTitle: "Pregabalin",
    cardKind: "intervention",
    blockedByFlags: ["pregabalinAllergy"],
    title: "Pregabalin not indicated",
    strength: "caution",
    summary: "Not indicated for patients above 75 or with eGFR below 30.",
    details: [],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { egfr: ["20to30", "lt20"] },
  },
  {
    id: "pre-gabapentin-alternative",
    routeIds: ["before-surgery"],
    cardId: "pre-gabapentin",
    cardTitle: "Gabapentin",
    cardKind: "intervention",
    blockedByFlags: ["gabapentinAllergy"],
    title: "Gabapentin alternative",
    strength: "consider",
    summary: "Gabapentin 300 mg PO four hours before surgery.",
    details: ["When pregabalin is unsuitable. Do not co-administer with pregabalin. Refer these patients to the Acute Pain Management Team for follow-up."],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { age: ["under75"], egfr: ["gt40", "30to40"], flagsAny: ["pregabalinAllergy"] },
  },
  {
    id: "pre-gabapentin-age-exclusion",
    routeIds: ["before-surgery"],
    cardId: "pre-gabapentin",
    cardTitle: "Gabapentin",
    cardKind: "intervention",
    blockedByFlags: ["gabapentinAllergy"],
    title: "Gabapentin not indicated",
    strength: "caution",
    summary: "Not indicated for patients above 75 or with eGFR below 30.",
    details: [],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { age: ["over75"] },
  },
  {
    id: "pre-gabapentin-renal-exclusion",
    routeIds: ["before-surgery"],
    cardId: "pre-gabapentin",
    cardTitle: "Gabapentin",
    cardKind: "intervention",
    blockedByFlags: ["gabapentinAllergy"],
    title: "Gabapentin not indicated",
    strength: "caution",
    summary: "Not indicated for patients above 75 or with eGFR below 30.",
    details: [],
    sourceRefs: [{ page: 3, section: "3.1 Pre-operative guideline" }],
    criteria: { egfr: ["20to30", "lt20"] },
  },
  {
    id: "during-surgery-next-phase",
    routeIds: ["during-surgery"],
    cardId: "during-surgery-next-phase",
    cardTitle: "Progress to post-operative phase",
    cardKind: "target",
    title: "Progress to post-operative phase",
    strength: "recommended",
    summary: "When intra-operative analgesic choices, adjuncts, regional techniques, and cautions have been reviewed.",
    details: [],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-fentanyl",
    routeIds: ["during-surgery"],
    cardId: "intra-fentanyl",
    cardTitle: "Fentanyl",
    cardKind: "intervention",
    choiceGroup: "intra-opioid",
    choiceValue: "fentanyl",
    blockedByFlags: ["fentanylAllergy"],
    title: "Fentanyl intra-operatively",
    strength: "recommended",
    summary: "Use fentanyl intra-operatively for analgesia.",
    details: [],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-alfentanil",
    routeIds: ["during-surgery"],
    cardId: "intra-alfentanil",
    cardTitle: "Alfentanil",
    cardKind: "intervention",
    choiceGroup: "intra-opioid",
    choiceValue: "alfentanil",
    blockedByFlags: ["alfentanilAllergy"],
    title: "Alfentanil intra-operatively",
    strength: "recommended",
    summary: "Use alfentanil intra-operatively for analgesia.",
    details: [],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-remifentanil",
    routeIds: ["during-surgery"],
    cardId: "intra-remifentanil",
    cardTitle: "Remifentanil",
    cardKind: "intervention",
    choiceGroup: "intra-opioid",
    choiceValue: "remifentanil",
    blockedByFlags: ["remifentanilAllergy"],
    title: "Remifentanil intra-operatively",
    strength: "caution",
    summary: "If remifentanil is used, limit the dose to less than 0.15 mcg/kg/min.",
    details: [],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-remifentanil-ppp-risk",
    routeIds: ["during-surgery"],
    cardId: "intra-remifentanil",
    cardTitle: "Remifentanil",
    cardKind: "intervention",
    choiceGroup: "intra-opioid",
    choiceValue: "remifentanil",
    blockedByFlags: ["remifentanilAllergy"],
    title: "Persistent post-operative pain risk",
    strength: "caution",
    summary: "Preferentially avoid remifentanil when persistent post-operative pain risk is present.",
    details: ["Risk factors include anxiety/depression, post-traumatic stress, pain catastrophising, chronic pain, pre-surgery opioid use, younger age, female gender, and longer surgery."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
    criteria: { flagsAny: ["persistentPostOpPainRisk"] },
  },
  {
    id: "intra-magnesium",
    routeIds: ["during-surgery"],
    cardId: "intra-magnesium",
    cardTitle: "Magnesium sulphate",
    cardKind: "intervention",
    blockedByFlags: ["magnesiumAllergy"],
    title: "Magnesium sulphate",
    strength: "recommended",
    summary: "Use up to 5 g MgSO4 IV intra-operatively.",
    details: [],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-dexmedetomidine",
    routeIds: ["during-surgery"],
    cardId: "intra-dexmedetomidine",
    cardTitle: "Dexmedetomidine adjunct",
    cardKind: "intervention",
    blockedByFlags: ["dexmedetomidineAllergy"],
    title: "Dexmedetomidine adjunct",
    strength: "consider",
    summary: "Dose 0.5 mcg/kg/hour IV infusion, with a stated range of 0.4-0.7 mcg/kg/hour.",
    details: ["Start as soon as central venous access is secured.", "Continue throughout the case until admission to ITU."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-dexmedetomidine-avoid",
    routeIds: ["during-surgery"],
    cardId: "intra-dexmedetomidine",
    cardTitle: "Dexmedetomidine adjunct",
    cardKind: "intervention",
    blockedByFlags: ["dexmedetomidineAllergy"],
    title: "Dexmedetomidine avoid criteria",
    strength: "caution",
    summary: "Avoid dexmedetomidine when high-degree heart block, severe bradycardia, or hypotension requiring noradrenaline above 0.1 mcg/kg/min is present.",
    details: [],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
    criteria: { flagsAny: ["highDegreeHeartBlock", "severeBradycardia", "hypotensionNoradrenaline"] },
  },
  {
    id: "intra-ketamine",
    routeIds: ["during-surgery"],
    cardId: "intra-ketamine",
    cardTitle: "Ketamine",
    cardKind: "intervention",
    blockedByFlags: ["ketamineAllergy"],
    title: "Ketamine intra-operatively",
    strength: "consider",
    summary: "Consider ketamine 0.5 mg/kg IV as a single dose or two divided doses.",
    details: [],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
    criteria: { flagsAny: ["chronicPain", "opioidUse", "methadone"] },
  },
  {
    id: "intra-rectus-sheath-block",
    routeIds: ["during-surgery"],
    cardId: "intra-rectus-sheath-block",
    cardTitle: "Rectus sheath block",
    cardKind: "intervention",
    title: "Rectus sheath block",
    strength: "consider",
    summary: "Bilateral posterior rectus sheath injection.",
    details: ["Use 10-20 ml local anaesthetic per side.", "Maximum levobupivacaine/bupivacaine dose: 2 mg/kg."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "intra-pifb",
    routeIds: ["during-surgery"],
    cardId: "intra-pifb",
    cardTitle: "Pecto-intercostal fascial block",
    cardKind: "intervention",
    title: "Pecto-intercostal fascial block",
    strength: "consider",
    summary: "Single-level bilateral T4 injection.",
    details: ["Use 10-20 ml local anaesthetic per side.", "Maximum levobupivacaine/bupivacaine dose: 2 mg/kg."],
    sourceRefs: [{ page: 4, section: "3.2 Intra-operative guideline" }],
  },
  {
    id: "handover-next-phase",
    routeIds: ["handover"],
    cardId: "handover-next-phase",
    cardTitle: "Progress to sedated phase",
    cardKind: "target",
    title: "Progress to sedated phase",
    strength: "recommended",
    summary: "When end-of-case opioid loading has been selected and immediate pain and sedation targets are ready to monitor.",
    details: [],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "handover-morphine",
    routeIds: ["handover"],
    cardId: "handover-morphine",
    cardTitle: "Morphine",
    cardKind: "intervention",
    choiceGroup: "handover-opioid",
    choiceValue: "morphine",
    blockedByFlags: ["morphineAllergy"],
    title: "Morphine opioid loading",
    strength: "recommended",
    summary: "If eGFR above 30: Morphine 0.1-0.2 mg/kg IV.",
    details: ["A loading dose of a long-acting opioid should be administered at the end of surgery."],
    sourceRefs: [{ page: 4, section: "3.2 Intravenous opioid loading" }, { page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { egfr: ["gt40", "30to40"] },
  },
  {
    id: "handover-oxycodone-above30",
    routeIds: ["handover"],
    cardId: "handover-oxycodone",
    cardTitle: "Oxycodone",
    cardKind: "intervention",
    choiceGroup: "handover-opioid",
    choiceValue: "oxycodone",
    blockedByFlags: ["oxycodoneAllergy"],
    title: "Oxycodone opioid loading: eGFR above 30",
    strength: "recommended",
    summary: "If eGFR above 30: Oxycodone 0.1-0.2 mg/kg IV.",
    details: ["A loading dose of a long-acting opioid should be administered at the end of surgery."],
    sourceRefs: [{ page: 4, section: "3.2 Intravenous opioid loading" }, { page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { egfr: ["gt40", "30to40"] },
  },
  {
    id: "handover-oxycodone-20-30",
    routeIds: ["handover"],
    cardId: "handover-oxycodone",
    cardTitle: "Oxycodone",
    cardKind: "intervention",
    choiceGroup: "handover-opioid",
    choiceValue: "oxycodone",
    blockedByFlags: ["oxycodoneAllergy"],
    title: "Oxycodone opioid loading: eGFR 20-30",
    strength: "recommended",
    summary: "If eGFR 20-30: Oxycodone 0.1-0.2 mg/kg IV.",
    details: [],
    sourceRefs: [{ page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { egfr: ["20to30"] },
  },
  {
    id: "handover-fentanyl-20-30",
    routeIds: ["handover"],
    cardId: "handover-fentanyl",
    cardTitle: "Fentanyl",
    cardKind: "intervention",
    choiceGroup: "handover-opioid",
    choiceValue: "fentanyl",
    blockedByFlags: ["fentanylAllergy"],
    title: "Fentanyl opioid loading: eGFR 20-30",
    strength: "recommended",
    summary: "If eGFR 20-30: Fentanyl 2-3 mcg/kg IV.",
    details: [],
    sourceRefs: [{ page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { egfr: ["20to30"] },
  },
  {
    id: "handover-fentanyl-lt20",
    routeIds: ["handover"],
    cardId: "handover-fentanyl",
    cardTitle: "Fentanyl",
    cardKind: "intervention",
    choiceGroup: "handover-opioid",
    choiceValue: "fentanyl",
    blockedByFlags: ["fentanylAllergy"],
    title: "Fentanyl opioid loading: eGFR below 20 or ESRF",
    strength: "recommended",
    summary: "If eGFR below 20 or ESRF: Fentanyl 2-3 mcg/kg IV.",
    details: [],
    sourceRefs: [{ page: 5, section: "3.2 Intravenous opioid loading continued" }],
    criteria: { egfr: ["lt20"] },
  },
  {
    id: "sedated-targets",
    routeIds: ["handover", "itu-sedated"],
    cardId: "sedated-targets",
    cardTitle: "Pain and sedation targets",
    cardKind: "target",
    title: "Immediate post-operative pain and sedation targets",
    strength: "recommended",
    summary: "Aim for RASS 0 to +1, CPOT 2 or less, NPS below 4 or mild pain.",
    details: ["Document pain scores hourly."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "sedated-propofol",
    routeIds: ["handover", "itu-sedated"],
    cardId: "sedated-propofol",
    cardTitle: "Propofol",
    cardKind: "intervention",
    title: "Post-operative sedation",
    strength: "recommended",
    summary: "Propofol infusion 1 mg/kg/hour IV.",
    details: ["Stop propofol infusion based on the ERACS pathway in preparation for extubation."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "itu-sedated-next-phase",
    routeIds: ["itu-sedated"],
    cardId: "itu-sedated-next-phase",
    cardTitle: "Progress to extubated phase",
    cardKind: "target",
    title: "Progress to extubated phase",
    strength: "recommended",
    summary: "When extubation is being prepared, sedation is being reduced, and CPOT/NPS targets remain appropriate.",
    details: [],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "sedated-dexmedetomidine",
    routeIds: ["itu-sedated"],
    cardId: "sedated-dexmedetomidine",
    cardTitle: "Dexmedetomidine",
    cardKind: "intervention",
    title: "Dexmedetomidine while sedated",
    strength: "recommended",
    summary: "Continue dexmedetomidine infusion at 0.1-0.4 mcg/kg/hour IV.",
    details: [
      "If the patient is not waking within one hour after stopping propofol, reduce dexmedetomidine by 50%.",
      "Stop dexmedetomidine one hour after the first oral analgesia has been given.",
      "Continue dexmedetomidine for a maximum of four hours after extubation.",
    ],
    cautions: ["Stop if RASS below -1, respiratory rate below 10, respiratory acidosis, systolic BP below 90 mmHg, or HR below 50."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
  },
  {
    id: "sedated-breakthrough-morphine",
    routeIds: ["itu-sedated"],
    cardId: "breakthrough-morphine",
    cardTitle: "Morphine",
    cardKind: "intervention",
    title: "Breakthrough pain: morphine",
    strength: "recommended",
    summary: "If pain management is challenging, use morphine 2 mg IV bolus every 2-5 minutes up to 20 mg.",
    details: ["Use the same opioid as administered intra-operatively for opioid loading until pain control is achieved."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["morphine"] },
  },
  {
    id: "sedated-breakthrough-oxycodone",
    routeIds: ["itu-sedated"],
    cardId: "breakthrough-oxycodone",
    cardTitle: "Oxycodone",
    cardKind: "intervention",
    title: "Breakthrough pain: oxycodone",
    strength: "recommended",
    summary: "If pain management is challenging, use oxycodone 2 mg IV bolus every 2-5 minutes up to 20 mg.",
    details: ["Use the same opioid as administered intra-operatively for opioid loading until pain control is achieved."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["oxycodone"] },
  },
  {
    id: "sedated-breakthrough-fentanyl",
    routeIds: ["itu-sedated"],
    cardId: "breakthrough-fentanyl",
    cardTitle: "Fentanyl",
    cardKind: "intervention",
    title: "Breakthrough pain: fentanyl",
    strength: "recommended",
    summary: "If pain management is challenging, use fentanyl 20 mcg IV bolus every 2-5 minutes up to 200 mcg.",
    details: ["Use the same opioid as administered intra-operatively for opioid loading until pain control is achieved."],
    sourceRefs: [{ page: 5, section: "3.3 Post-operative guideline" }],
    criteria: { opioid: ["fentanyl"] },
  },
  {
    id: "extubated-targets",
    routeIds: ["after-extubation"],
    cardId: "extubated-targets",
    cardTitle: "Pain and sedation targets",
    cardKind: "target",
    title: "After extubation targets",
    strength: "recommended",
    summary: "Aim for RASS 0 to +2 and CPOT 2 or less, NPS below 4, or mild pain.",
    details: [],
    sourceRefs: [{ page: 6, section: "After extubation" }],
  },
  {
    id: "safe-for-oral-analgesia",
    routeIds: ["after-extubation"],
    cardId: "safe-for-oral-analgesia",
    cardTitle: "Progress to oral analgesia phase",
    cardKind: "target",
    title: "Progress to oral analgesia phase",
    strength: "recommended",
    summary: "When it is safe for the patient to take sips of water.",
    details: [],
    sourceRefs: [{ page: 6, section: "After extubation" }],
  },
  {
    id: "oral-analgesia-next-phase",
    routeIds: ["oral-analgesia"],
    cardId: "oral-analgesia-next-phase",
    cardTitle: "Continue or escalate",
    cardKind: "target",
    title: "Before leaving oral analgesia",
    strength: "recommended",
    summary: "Continue the oral regimen while targets are met; use escalation guidance if NPS is above 4, pain is severe, function is limited, or oral analgesia is not tolerated.",
    details: [],
    sourceRefs: [{ page: 6, section: "PCA escalation" }],
  },
  {
    id: "oral-analgesia-gt30",
    routeIds: ["oral-analgesia"],
    cardId: "oral-analgesia",
    cardTitle: "Oral analgesia",
    cardKind: "intervention",
    choiceGroup: "oral-analgesia-option",
    title: "Oral analgesia: eGFR above 30",
    strength: "recommended",
    blockedByFlags: ["dihydrocodeineAllergy", "morphineAllergy"],
    summary: "If eGFR above 30: Dihydrocodeine 30-60 mg PO four times daily; IR morphine 10-20 mg PO 2-hourly PRN.",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["gt40", "30to40"] },
  },
  {
    id: "oral-analgesia-20-30",
    routeIds: ["oral-analgesia"],
    cardId: "oral-analgesia",
    cardTitle: "Oral analgesia",
    cardKind: "intervention",
    choiceGroup: "oral-analgesia-option",
    title: "Oral analgesia: eGFR 20-30",
    strength: "recommended",
    blockedByFlags: ["oxycodoneAllergy"],
    summary: "If eGFR 20-30: IR oxycodone 2.5 mg PO four times daily; IR oxycodone 2.5-5 mg PO 2-hourly PRN.",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["20to30"] },
  },
  {
    id: "oral-analgesia-lt20",
    routeIds: ["oral-analgesia"],
    cardId: "oral-analgesia",
    cardTitle: "Oral analgesia",
    cardKind: "intervention",
    choiceGroup: "oral-analgesia-option",
    title: "Oral analgesia: eGFR below 20 or ESRF",
    strength: "recommended",
    blockedByFlags: ["buprenorphineAllergy"],
    summary: "If eGFR below 20 or ESRF: Buprenorphine 200 mcg SL four times daily; buprenorphine 200 mcg SL 4-hourly PRN.",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { egfr: ["lt20"] },
  },
  {
    id: "oral-paracetamol-over-50kg",
    routeIds: ["oral-analgesia"],
    cardId: "oral-paracetamol",
    cardTitle: "Paracetamol",
    cardKind: "intervention",
    blockedByFlags: ["paracetamolAllergy"],
    title: "Regular oral paracetamol",
    strength: "recommended",
    summary: "For weight above 50 kg, give paracetamol 1 g PO four times daily.",
    details: ["Give oral analgesia once it is safe to take sips of water."],
    sourceRefs: [{ page: 6, section: "Oral analgesia guideline" }],
    criteria: { flagsAny: ["weightAbove50"] },
  },
  {
    id: "nsaid-caution",
    routeIds: ["oral-analgesia"],
    cardId: "nsaid-caution",
    cardTitle: "NSAIDs",
    cardKind: "intervention",
    blockedByFlags: ["nsaidAllergy"],
    title: "NSAID caution",
    strength: "caution",
    summary: "Ibuprofen 400 mg PO three times daily is listed, but ACCU consultant/senior physician review is required before considering this.",
    details: ["Give oral analgesia once it is safe to take sips of water.", "Limit NSAID therapy to a maximum of 72 hours."],
    sourceRefs: [{ page: 6, section: "NSAIDs" }],
  },
  {
    id: "nsaid-contraindication",
    routeIds: ["oral-analgesia"],
    cardId: "nsaid-caution",
    cardTitle: "NSAIDs",
    cardKind: "intervention",
    blockedByFlags: ["nsaidAllergy"],
    title: "NSAID contraindication present",
    strength: "caution",
    summary: "Avoid NSAIDs when a listed contraindication is present.",
    details: [],
    sourceRefs: [{ page: 6, section: "NSAIDs" }],
    criteria: {
      flagsAny: [
        "ischaemicHeartDisease",
        "congestiveHeartFailure",
        "cerebrovascularDisease",
        "peripheralVascularDisease",
        "renalImpairment",
        "giBleedingHistory",
        "coagulationDefect",
        "uncontrolledHypertension",
      ],
    },
  },
  {
    id: "pca-trigger",
    routeIds: ["after-extubation", "oral-analgesia"],
    cardId: "pca",
    cardTitle: "PCA",
    cardKind: "intervention",
    title: "Consider PCA",
    strength: "escalate",
    summary: "Consider PCA if NPS is above 4 or pain is severe, significant functional limitation persists despite regular oral analgesia and maximum top-up IV boluses, or the oral route is not tolerated.",
    details: [],
    sourceRefs: [{ page: 6, section: "PCA escalation" }],
    criteria: {
      any: [{ painAtLeast: 5 }, { flagsAny: ["oralNotTolerated", "functionalLimitation"] }],
    },
  },
  {
    id: "postop-ketamine",
    routeIds: ["after-extubation", "oral-analgesia"],
    cardId: "postop-ketamine",
    cardTitle: "Ketamine",
    cardKind: "intervention",
    blockedByFlags: ["ketamineAllergy"],
    title: "Ketamine escalation",
    strength: "escalate",
    summary: "Discuss with the ITU consultant before starting ketamine for challenging pain with significant functional limitation.",
    details: [
      "Use IV ketamine 0.1-0.3 mg/kg/hour for 24-48 hours in Level 2 or 3 wards.",
      "Then transition to oral ketamine; give the first oral dose one hour after stopping the infusion.",
      "Give oral ketamine 50 mg PO four times daily, tapering over 48-72 hours.",
      "Refer to the Acute Pain Management Team for follow-up.",
    ],
    sourceRefs: [{ page: 6, section: "Ketamine" }, { page: 7, section: "Ketamine continued" }],
    criteria: { flagsAny: ["chronicPain", "opioidUse", "methadone", "ivdu", "mentalHealth", "functionalLimitation"] },
  },
  {
    id: "postop-gabapentinoids",
    routeIds: ["oral-analgesia"],
    cardId: "postop-gabapentinoids",
    cardTitle: "Gabapentinoids",
    cardKind: "intervention",
    blockedByFlags: ["pregabalinAllergy"],
    title: "Gabapentinoids for neuropathic features",
    strength: "escalate",
    summary: "Discuss with Acute Pain Management Team before starting post-operative gabapentinoids for challenging pain with neuropathic features.",
    details: [
      "For eGFR above 40, the printed guideline lists pregabalin 75 mg twice daily PO.",
      "For eGFR 30-40, the printed guideline lists pregabalin 50 mg twice daily PO.",
      "Duration of therapy is listed as 4-6 weeks.",
    ],
    cautions: ["Common side-effects include drowsiness, dizziness, confusion, and malaise. If present, omit morning dose and consider reducing evening dose."],
    sourceRefs: [{ page: 7, section: "Gabapentinoids" }],
    criteria: { flagsAny: ["neuropathicPain"] },
  },
  {
    id: "non-pharm",
    routeIds: ["after-extubation", "oral-analgesia"],
    cardId: "non-pharm",
    cardTitle: "Non-pharmacological approaches",
    cardKind: "intervention",
    title: "Non-pharmacological adjuncts",
    strength: "recommended",
    summary: "Non-pharmacological therapy, such as music and massage, is recommended as an adjunct to pharmacological therapy.",
    details: ["Use alongside pharmacological treatment rather than as a substitute when pain is clinically significant."],
    sourceRefs: [{ page: 7, section: "Non-pharmacological approaches" }],
  },
];

export const painScaleRows = [
  { range: "0", label: "No pain" },
  { range: "1-3", label: "Mild pain" },
  { range: "4-6", label: "Moderate pain" },
  { range: "7-10", label: "Severe pain" },
];

export const functionalAssessment = [
  {
    grade: "A",
    label: "No limitation",
    description: "The patient can undertake the activity without limitation due to pain, with pain intensity typically 0-3.",
  },
  {
    grade: "B",
    label: "Mild limitation",
    description: "The patient can undertake the activity but experiences moderate to severe pain, with pain intensity typically 4-10.",
  },
  {
    grade: "C",
    label: "Significant limitation",
    description: "The patient is unable to complete the activity due to pain or pain-treatment adverse effects, independent of pain intensity score.",
  },
];

export const cpotIndicators: CpotIndicator[] = [
  {
    id: "facial",
    label: "Facial expression",
    patientGroup: "all",
    options: [
      { label: "Relaxed, neutral", score: 0, description: "No muscle tension observed." },
      { label: "Tense", score: 1, description: "Frowning, brow lowering, orbit tightening, levator contraction, or eye opening/tearing during nociception." },
      { label: "Grimacing", score: 2, description: "Clear grimacing expression." },
    ],
  },
  {
    id: "movement",
    label: "Body movements",
    patientGroup: "all",
    options: [
      { label: "Absence of movements or normal position", score: 0, description: "No movement or normal movement not aimed toward the pain site." },
      { label: "Protection", score: 1, description: "Slow cautious movements, touching or rubbing pain site, or seeking attention through movements." },
      { label: "Restlessness / agitation", score: 2, description: "Pulling tube, attempting to sit up, thrashing, not following commands, striking staff, or trying to climb out of bed." },
    ],
  },
  {
    id: "ventilator",
    label: "Compliance with ventilator",
    patientGroup: "intubated",
    options: [
      { label: "Tolerating ventilator or movement", score: 0, description: "Alarms not activated, easy ventilation." },
      { label: "Coughing but tolerating", score: 1, description: "Coughing; alarms may activate but stop spontaneously." },
      { label: "Fighting ventilator", score: 2, description: "Asynchrony, blocking ventilation, alarms frequently activated." },
    ],
  },
  {
    id: "vocalisation",
    label: "Vocalisation",
    patientGroup: "extubated",
    options: [
      { label: "Normal tone or no sound", score: 0, description: "Talking in a normal tone or no sound." },
      { label: "Sighing, moaning", score: 1, description: "Audible sighing or moaning." },
      { label: "Crying out, sobbing", score: 2, description: "Crying out or sobbing." },
    ],
  },
  {
    id: "muscle",
    label: "Muscle tension",
    patientGroup: "all",
    options: [
      { label: "Relaxed", score: 0, description: "No resistance to passive movements." },
      { label: "Tense, rigid", score: 1, description: "Resistance to passive movements." },
      { label: "Very tense or rigid", score: 2, description: "Strong resistance to passive movements or incapacity to complete them." },
    ],
  },
];

export function criteriaMatches(
  criteria: RecommendationCriteria | undefined,
  state: WorkflowState,
): { isMatch: boolean; missingContext: string[]; hasHardMismatch: boolean } {
  const missingContext: string[] = [];
  if (!criteria) {
    return { isMatch: true, missingContext, hasHardMismatch: false };
  }

  if (criteria.any && criteria.any.length > 0) {
    const branchResults = criteria.any.map((branch) => criteriaMatches(branch, state));
    if (branchResults.some((result) => result.isMatch)) {
      return { isMatch: true, missingContext, hasHardMismatch: false };
    }

    const possibleBranches = branchResults.filter((result) => !result.hasHardMismatch);
    if (possibleBranches.length > 0) {
      return {
        isMatch: false,
        missingContext: Array.from(new Set(possibleBranches.flatMap((result) => result.missingContext))),
        hasHardMismatch: false,
      };
    }

    return { isMatch: false, missingContext, hasHardMismatch: true };
  }

  let hasHardMismatch = false;
  if (criteria.age) {
    if (state.age === "unknown") {
      missingContext.push("age band");
    } else if (!criteria.age.includes(state.age)) {
      hasHardMismatch = true;
    }
  }

  if (criteria.egfr) {
    if (state.egfr === "unknown") {
      missingContext.push("eGFR band");
    } else if (!criteria.egfr.includes(state.egfr)) {
      hasHardMismatch = true;
    }
  }

  if (criteria.sedation) {
    if (state.sedation === "unknown") {
      missingContext.push("sedation state");
    } else if (!criteria.sedation.includes(state.sedation)) {
      hasHardMismatch = true;
    }
  }

  if (criteria.opioid) {
    if (state.opioid === "unknown") {
      missingContext.push("intra-operative opioid");
    } else if (!criteria.opioid.includes(state.opioid)) {
      hasHardMismatch = true;
    }
  }

  if (criteria.flagsAny && criteria.flagsAny.length > 0) {
    const hasFlag = criteria.flagsAny.some((flag) => state.flags.includes(flag as never));
    if (!hasFlag) {
      const allAnsweredNo = criteria.flagsAny.every((flag) => state.noFlags.includes(flag as never));
      if (allAnsweredNo) {
        hasHardMismatch = true;
      } else {
        missingContext.push("relevant patient factors");
      }
    }
  }

  if (criteria.painAtLeast !== undefined) {
    if (state.pain === null) {
      missingContext.push("pain score");
    } else if (state.pain < criteria.painAtLeast) {
      hasHardMismatch = true;
    }
  }

  if (criteria.cpotAtLeast !== undefined) {
    if (state.cpot === null) {
      missingContext.push("CPOT");
    } else if (state.cpot < criteria.cpotAtLeast) {
      hasHardMismatch = true;
    }
  }

  return { isMatch: !hasHardMismatch && missingContext.length === 0, missingContext, hasHardMismatch };
}

export function visibleRecommendations(routeId: WorkflowRouteId, state: WorkflowState): VisibleRecommendation[] {
  return recommendations
    .filter((recommendation) => recommendation.routeIds.includes(routeId))
    .map((recommendation) => {
      const result = criteriaMatches(recommendation.criteria, state);
      return {
        ...recommendation,
        isContextualMatch: result.isMatch,
        missingContext: result.missingContext,
        hasHardMismatch: result.hasHardMismatch,
      };
    })
    .filter((recommendation) => recommendation.isContextualMatch || (recommendation.missingContext.length > 0 && !recommendation.hasHardMismatch));
}
